<template>
<div class="lh-root" :style="themePageStyle">

  <nav class="lh-topbar">
    <div class="lh-tb-inner">
      <button class="lh-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" class="lh-logo" /></a>
      <span class="lh-tb-title">Learning Hub</span>
      <div class="lh-search">
        <i class="mdi mdi-magnify"></i>
        <input v-model="search" placeholder="Search articles" @keyup.enter="loadArticles" @input="onSearchInput" />
      </div>
      <button class="lh-tb-feed" @click="$router.push('/forum')"><i class="mdi mdi-home"></i> Feed</button>
    </div>
  </nav>

  <div class="lh-wrap">

    <!-- categories -->
    <div class="lh-chips">
      <button class="lh-chip" :class="{active:!filter.categorySlug}" @click="filter.categorySlug='';loadArticles()">All topics</button>
      <button v-for="c in categories" :key="c.id" class="lh-chip" :class="{active:filter.categorySlug===c.slug}"
              @click="filter.categorySlug = filter.categorySlug===c.slug ? '' : c.slug; loadArticles()">
        <i class="mdi" :class="catIcon(c.name)"></i> {{ c.name }}
      </button>
    </div>

    <!-- featured hero -->
    <div v-if="featured.length && !filter.categorySlug && !search" class="lh-feat-wrap">
      <div class="lh-feat-hero" @click="openArticle(featured[0])">
        <div class="lh-feat-bg" :style="coverStyle(featured[0])"></div>
        <div class="lh-feat-overlay">
          <span class="lh-badge"><i class="mdi mdi-star"></i> Featured</span>
          <span class="lh-cat">{{ featured[0].category_name }}</span>
          <div class="lh-feat-title">{{ featured[0].title }}</div>
          <p class="lh-feat-excerpt">{{ featured[0].excerpt }}</p>
          <div class="lh-feat-meta">
            <span><i class="mdi mdi-eye-outline"></i> {{ featured[0].view_count }}</span>
            <span><i class="mdi mdi-heart-outline"></i> {{ featured[0].like_count }}</span>
            <span v-if="featured[0].published_at"><i class="mdi mdi-calendar"></i> {{ fmtDate(featured[0].published_at) }}</span>
          </div>
        </div>
      </div>
      <div class="lh-feat-side">
        <div v-for="a in featured.slice(1,3)" :key="'fs-'+a.id" class="lh-feat-mini" @click="openArticle(a)">
          <div class="lh-feat-mini-bg" :style="coverStyle(a)"></div>
          <div class="lh-feat-mini-body">
            <span class="lh-cat sm">{{ a.category_name }}</span>
            <div class="lh-feat-mini-title">{{ a.title }}</div>
            <div class="lh-feat-mini-meta"><i class="mdi mdi-eye-outline"></i> {{ a.view_count }} · <i class="mdi mdi-heart-outline"></i> {{ a.like_count }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- all -->
    <div class="lh-sec-head">
      <h6>{{ filter.categorySlug ? activeCategoryName : (search ? 'Search results' : 'Latest articles') }}</h6>
    </div>

    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!articles.length" class="lh-empty">
      <i class="mdi mdi-book-search-outline"></i>
      <h5>No articles match</h5>
      <p>Try another topic or search term.</p>
    </div>
    <div v-else class="lh-grid">
      <div v-for="a in articles" :key="a.id" class="lh-card" @click="openArticle(a)">
        <div class="lh-cover" :style="coverStyle(a)">
          <i v-if="!a.cover_image_url" class="mdi" :class="catIcon(a.category_name)"></i>
          <span v-if="a.target_audience" class="lh-aud">{{ humanize(a.target_audience) }}</span>
        </div>
        <div class="lh-body">
          <span class="lh-cat">{{ a.category_name }}</span>
          <div class="lh-title">{{ a.title }}</div>
          <p class="lh-excerpt">{{ a.excerpt }}</p>
          <div class="lh-meta">
            <span><i class="mdi mdi-eye-outline"></i> {{ a.view_count }}</span>
            <span><i class="mdi mdi-heart-outline"></i> {{ a.like_count }}</span>
            <span v-if="a.published_at"><i class="mdi mdi-calendar"></i> {{ fmtDate(a.published_at) }}</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F0EBE4;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important}
html[data-theme="light"] .lh-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .lh-topbar{background:#FFFFFF!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .lh-card,
html[data-theme="light"] .lh-feat-mini{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .lh-search,
html[data-theme="light"] .lh-chip{background:#EBE7E1!important;color:#1A1A1A!important;border-color:#E2DCD3!important}
</style>

<style scoped>
.lh-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.lh-topbar{position:sticky;top:0;z-index:100;background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.lh-tb-inner{display:flex;align-items:center;gap:12px;max-width:1040px;margin:0 auto;padding:10px 14px}
.lh-back{width:38px;height:38px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px}
.lh-logo{height:30px}
.lh-tb-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;white-space:nowrap}
.lh-search{flex:1;display:flex;align-items:center;gap:8px;background:var(--fm-bg2,#0F0F0F);border-radius:22px;padding:8px 14px;max-width:340px;margin-left:auto}
.lh-search i{color:var(--fm-s,#6B6356);font-size:18px}
.lh-search input{flex:1;background:transparent;border:none;outline:none;color:var(--fm-h,#F5F0E8);font-size:14px}
.lh-tb-feed{background:rgba(191,161,100,.12);border:none;color:#BFA164;font-weight:700;font-size:13px;padding:8px 12px;border-radius:8px;white-space:nowrap}
.lh-wrap{max-width:1040px;margin:0 auto;padding:16px 14px 60px}

.lh-chips{display:flex;gap:8px;overflow-x:auto;padding-bottom:6px;margin-bottom:16px;scrollbar-width:thin}
.lh-chip{flex-shrink:0;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:20px;padding:7px 14px;font-size:13px;font-weight:600;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.lh-chip.active{background:rgba(191,161,100,.14);border-color:#BFA164;color:#BFA164}

/* featured */
.lh-feat-wrap{display:grid;grid-template-columns:1.6fr 1fr;gap:14px;margin-bottom:24px}
.lh-feat-hero{position:relative;height:320px;border-radius:16px;overflow:hidden;cursor:pointer}
.lh-feat-bg{position:absolute;inset:0;background-size:cover;background-position:center}
.lh-feat-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.92) 0%,rgba(0,0,0,.55) 35%,rgba(0,0,0,.2) 65%,rgba(0,0,0,.45) 100%);display:flex;flex-direction:column;justify-content:flex-end;padding:20px}
.lh-badge{align-self:flex-start;background:#FFD54A;color:#1A1A1A;font-size:11px;font-weight:800;padding:3px 10px;border-radius:6px;display:inline-flex;align-items:center;gap:4px;margin-bottom:8px}
.lh-feat-title{color:#fff;font-weight:800;font-size:24px;line-height:1.2;margin:6px 0;text-shadow:0 1px 6px rgba(0,0,0,.6)}
.lh-feat-excerpt{color:#fff;font-size:14px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin-bottom:8px;text-shadow:0 1px 4px rgba(0,0,0,.7)}
.lh-feat-meta{display:flex;gap:14px;color:#fff;font-size:12px;text-shadow:0 1px 4px rgba(0,0,0,.7)}
.lh-feat-side{display:flex;flex-direction:column;gap:14px}
.lh-feat-mini{flex:1;display:flex;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;overflow:hidden;cursor:pointer;min-height:90px}
.lh-feat-mini:hover{border-color:#BFA164}
.lh-feat-mini-bg{width:110px;flex-shrink:0;background-size:cover;background-position:center;background-color:var(--fm-bg2,#0F0F0F)}
.lh-feat-mini-body{padding:10px 12px;display:flex;flex-direction:column;justify-content:center;min-width:0}
.lh-feat-mini-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;line-height:1.3;margin:3px 0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.lh-feat-mini-meta{color:var(--fm-s,#6B6356);font-size:11px}

.lh-cat{display:inline-block;font-size:10px;padding:2px 8px;border-radius:6px;background:rgba(191,161,100,.14);color:#BFA164;font-weight:700;text-transform:uppercase;letter-spacing:.3px}
.lh-cat.sm{font-size:9px;padding:1px 6px}

.lh-sec-head{margin-bottom:12px}
.lh-sec-head h6{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:16px;margin:0}

.lh-empty{text-align:center;padding:50px 20px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px}
.lh-empty i{font-size:60px;color:var(--fm-s,#3A3530)}
.lh-empty h5{color:var(--fm-h,#F5F0E8);margin-top:12px}
.lh-empty p{color:var(--fm-s,#6B6356);font-size:14px}

.lh-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.lh-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;overflow:hidden;cursor:pointer;transition:transform .15s,border-color .15s;display:flex;flex-direction:column}
.lh-card:hover{transform:translateY(-2px);border-color:#BFA164}
.lh-cover{height:150px;background-size:cover;background-position:center;background-color:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;font-size:44px;color:rgba(191,161,100,.5);position:relative}
.lh-aud{position:absolute;top:8px;right:8px;background:rgba(0,0,0,.6);color:#FFD54A;font-size:10px;padding:2px 8px;border-radius:6px;text-transform:uppercase;letter-spacing:.4px}
.lh-body{padding:14px;display:flex;flex-direction:column;flex:1}
.lh-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;line-height:1.35;margin:6px 0}
.lh-excerpt{color:var(--fm-m,#A09888);font-size:12.5px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;flex:1}
.lh-meta{display:flex;gap:12px;font-size:11px;color:var(--fm-s,#6B6356);padding-top:10px;border-top:1px solid var(--fm-br,#1E1E1E);margin-top:10px}
.lh-meta i{font-size:12px}

@media (max-width:720px){.lh-feat-wrap{grid-template-columns:1fr}.lh-feat-side{flex-direction:row}.lh-tb-title{display:none}}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  /* Tight topbar: smaller logo, search beside it, no large gaps */
  .lh-topbar { padding: 6px 0 !important; }
  .lh-tb-inner { gap: 8px !important; padding: 6px 10px !important; }
  .lh-back { width: 36px !important; height: 36px !important; font-size: 18px !important; }
  .lh-logo { height: 26px !important; }
  .lh-search { padding: 6px 10px !important; gap: 6px !important; min-width: 0; }
  .lh-search input { font-size: 12px !important; }
  .lh-tb-feed { font-size: 11px !important; padding: 6px 8px !important; }

  /* Container: trim top offset (sticky compact topbar), tight side padding */
  .lh-wrap { padding: 14px 10px calc(40px + env(safe-area-inset-bottom, 0px)) !important; max-width: 100% !important; box-sizing: border-box !important; overflow-x: hidden !important; }

  /* Category chips — horizontal scroll, smaller */
  .lh-chips { gap: 6px !important; margin-bottom: 12px !important; }
  .lh-chip { padding: 6px 10px !important; font-size: 11px !important; }

  /* Featured hero shrunk: stack vertically, smaller title */
  .lh-feat-wrap { gap: 10px !important; margin-bottom: 16px !important; min-width: 0 !important; }
  .lh-feat-hero { aspect-ratio: 16/10 !important; width: 100% !important; min-width: 0 !important; }
  .lh-feat-bg { width: 100% !important; }
  .lh-feat-title { font-size: 16px !important; line-height: 1.25 !important; }
  .lh-feat-excerpt { font-size: 11px !important; -webkit-line-clamp: 2 !important; margin-top: 4px !important; }
  .lh-feat-meta { font-size: 10px !important; gap: 8px !important; margin-top: 4px !important; }
  .lh-feat-overlay { padding: 12px !important; }
  .lh-badge { font-size: 9px !important; padding: 2px 6px !important; }
  .lh-cat { font-size: 9px !important; padding: 2px 6px !important; }

  /* Featured side tiles: side-by-side and tighter */
  .lh-feat-side { gap: 8px !important; }
  .lh-feat-mini { border-radius: 10px !important; flex: 1; }
  .lh-feat-mini-bg { height: 86px !important; }
  .lh-feat-mini-body { padding: 8px !important; }
  .lh-feat-mini-title { font-size: 11px !important; line-height: 1.3 !important; -webkit-line-clamp: 2 !important; }
  .lh-feat-mini-meta { font-size: 9px !important; }

  /* Section heading */
  .lh-sec-head h6 { font-size: 13px !important; margin-bottom: 8px !important; }

  /* Grid — 2 columns on phones (not single-column big cards) */
  .lh-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
  .lh-card { padding: 0 !important; border-radius: 10px !important; }
  .lh-cover { height: 100px !important; }
  .lh-body { padding: 8px 10px 10px !important; }
  .lh-title { font-size: 12px !important; line-height: 1.3 !important; margin-bottom: 3px !important; -webkit-line-clamp: 2 !important; }
  .lh-excerpt { font-size: 10px !important; -webkit-line-clamp: 2 !important; margin-bottom: 4px !important; }
  .lh-meta { font-size: 9px !important; gap: 6px !important; padding-top: 6px !important; margin-top: 6px !important; }
  .lh-meta i { font-size: 10px !important; }
  .lh-aud { font-size: 9px !important; padding: 1px 5px !important; }
}

/* Tighter still on small phones — collapse Feed CTA to icon */
@media (max-width: 420px) {
  .lh-tb-feed span:not(.mdi), .lh-tb-feed { font-size: 0 !important; padding: 6px !important; }
  .lh-tb-feed .mdi { font-size: 16px !important; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "LearningHub",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:false,search:'',_searchTimer:null,
    filter: { categorySlug:'' },
    categories: [], articles: [], featured: [],
    _grads:['linear-gradient(135deg,#2a2418,#14110a)','linear-gradient(135deg,#182a1a,#0a140b)','linear-gradient(135deg,#181f2a,#0a0d14)','linear-gradient(135deg,#2a182a,#140a14)','linear-gradient(135deg,#2a1818,#140a0a)']
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'},
    activeCategoryName(){var c=this.categories.find(x=>x.slug===this.filter.categorySlug);return c?c.name:'Articles';}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadCategories();
    this.loadFeatured();
    this.loadArticles();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/forum'); },
    imgUrl(u){if(!u)return'';if(u.indexOf('http')===0)return u;return uploads+String(u).replace(/^uploads\//,'');},
    humanize(s){return (s||'').charAt(0).toUpperCase()+ (s||'').slice(1);},
    fmtDate(ts){ if(!ts) return ''; var d=new Date(String(ts).replace(' ','T')); return d.toLocaleDateString('en',{day:'numeric',month:'short',year:'numeric'}); },
    catIcon(name){
      var n=(name||'').toLowerCase();
      if(n.indexOf('health')>-1||n.indexOf('disease')>-1) return 'mdi-heart-pulse';
      if(n.indexOf('feed')>-1||n.indexOf('nutri')>-1) return 'mdi-grain';
      if(n.indexOf('breed')>-1) return 'mdi-baby-carriage';
      if(n.indexOf('market')>-1||n.indexOf('price')>-1) return 'mdi-chart-line';
      if(n.indexOf('dairy')>-1||n.indexOf('milk')>-1) return 'mdi-cup-water';
      if(n.indexOf('poultry')>-1||n.indexOf('chick')>-1) return 'mdi-egg';
      if(n.indexOf('equip')>-1) return 'mdi-tractor';
      return 'mdi-book-open-variant';
    },
    coverStyle(a){
      if(a && a.cover_image_url) return 'background-image:url('+this.imgUrl(a.cover_image_url)+')';
      var id=Number(a&&a.id)||0; return 'background-image:'+this._grads[id % this._grads.length];
    },
    onSearchInput(){
      var self=this;
      if(this._searchTimer)clearTimeout(this._searchTimer);
      this._searchTimer=setTimeout(function(){ self.loadArticles(); }, 350);
    },
    async loadCategories(){
      try{var r=await executeJson({function:'getArticleCategories'},flocker);
        if(r.data&&r.data.success) this.categories=(r.data.data&&r.data.data.categories)||[];
      }catch(e){}
    },
    async loadFeatured(){
      try{var r=await executeJson({function:'getArticles',featuredOnly:1,limit:3},flocker);
        if(r.data&&r.data.success) this.featured=(r.data.data&&r.data.data.articles)||[];
      }catch(e){}
    },
    async loadArticles(){
      this.loading=true;
      try{
        var params={function:'getArticles',limit:30};
        if(this.filter.categorySlug) params.categorySlug=this.filter.categorySlug;
        if(this.search) params.search=this.search;
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success) this.articles=(r.data.data&&r.data.data.articles)||[];
      }catch(e){console.error(e)}
      this.loading=false;
    },
    openArticle(a){this.$router.push('/learn/'+(a.slug||a.id))}
  }
};
</script>
